import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { postLoginPath } from "../lib/roles";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}!`);
      nav(postLoginPath(u));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-aurora flex items-center justify-center p-6">
      <div className="w-full max-w-md glass-card-strong p-8" data-testid="login-page">
        <Link to="/" className="font-display text-2xl font-black tracking-tight inline-block mb-6">
          <span className="text-gradient-pink">S</span><span className="text-neoncyan">0</span><span className="text-gradient-pink">rted</span>
        </Link>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Welcome back</h1>
        <p className="text-white/60 mt-1 text-sm">Sign in to keep levelling up.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-white/60">Email</label>
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 outline-none focus:border-neonpink"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-white/60">Password</label>
            <input
              type="password" required value={password} onChange={e => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 outline-none focus:border-neonpink"
              placeholder="•••••••"
            />
          </div>
          {err && <div className="text-sm text-red-400" data-testid="login-error">{err}</div>}
          <button type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full btn-primary-neon disabled:opacity-50">
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-white/40">
          <div className="flex-1 h-px bg-white/10"/> OR <div className="flex-1 h-px bg-white/10"/>
        </div>

        <button onClick={googleSignIn} data-testid="google-signin-button"
          className="w-full btn-secondary-neon flex items-center justify-center gap-2">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#fff" d="M21.35 11.1H12v2.9h5.35c-.23 1.45-1.6 4.25-5.35 4.25-3.22 0-5.85-2.67-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.06.78 3.76 1.45l2.57-2.47C16.66 3.83 14.55 3 12 3 6.99 3 3 6.99 3 12s3.99 9 9 9c5.2 0 8.65-3.66 8.65-8.81 0-.59-.07-1.05-.3-2.09Z"/></svg>
          Continue with Google
        </button>

        <p className="text-sm text-white/60 text-center mt-6">
          New here? <Link to="/register" className="text-neoncyan font-semibold" data-testid="login-to-register">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
